import { useEffect, useRef, useState } from "react";
import { ChevronDown, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { errMsg } from "@/lib/api";
import { eur, pct, foodCostTone, unitLabel } from "@/lib/format";
import { track } from "@/lib/analytics";
import { t } from "@/i18n";

const STEPS = [t("aiFillDialog.lecture_de_vos_plats"), t("aiFillDialog.choix_des_ingredients"), t("aiFillDialog.estimation_des_quantites"), t("aiFillDialog.calcul_des_couts_matieres")];

function Loading() {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => Math.min(x + 1, STEPS.length - 1)), 3500); return () => clearInterval(t); }, []);
  return (
    <div className="flex flex-col items-center py-10 text-center" data-testid="ai-fill-loading">
      <div className="mb-4 rounded-2xl bg-[hsl(var(--ai)/0.12)] p-4"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--ai))]" /></div>
      <p className="font-semibold">{STEPS[i]}</p>
      <p className="mt-1 text-xs text-muted-foreground">{t("aiFillDialog.15_a_40_secondes_selon_le_nombre_de_plats")}</p>
    </div>
  );
}

function Row({ it, i, checked, onToggle, target }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="py-1" data-testid={`ai-fill-item-${i}`}>
      <div className="flex items-center gap-3 px-3 py-2">
        <Checkbox checked={checked} onCheckedChange={onToggle} data-testid={`ai-fill-checkbox-${i}`} />
        <button type="button" onClick={() => setOpen(!open)} className="flex min-w-0 flex-1 items-center gap-2 text-left" data-testid={`ai-fill-toggle-${i}`}>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">{it.name}</span>
            <span className="text-xs text-muted-foreground">{it.ingredients.length} {t("aiFillDialog.ingredients_cout")} {eur(it.estimated_cost)}</span>
          </span>
          {it.food_cost_pct != null && <span className={`num shrink-0 text-xs font-semibold ${foodCostTone(it.food_cost_pct, target)}`}>{t("common.fc_short")} {pct(it.food_cost_pct)}</span>}
          <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <ul className="mx-3 mb-2 space-y-1 rounded-lg bg-muted/50 p-3 text-xs">
          {it.ingredients.map((l, j) => (
            <li key={j} className="flex justify-between gap-3">
              <span className="min-w-0 truncate">{l.name}{!l.ingredient_id && <span className="ml-1.5 text-amber-600 dark:text-amber-400">{t("aiFillDialog.estime")}</span>}</span>
              <span className="num shrink-0 text-muted-foreground">{l.quantity} {unitLabel(l.unit)} · {eur(l.line_cost)}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function AiFillDialog({ open, onOpenChange, onDone, target }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (!open || started.current) return;
    started.current = true;
    api.post("/recipes/ai-suggest-ingredients", {}, { timeout: 120000 })
      .then(({ data }) => { setData(data); setSelected(new Set(data.items.map((x) => x.recipe_id))); })
      .catch((e) => setError(errMsg(e)));
  }, [open]);

  const toggle = (id) => setSelected((s) => { const x = new Set(s); x.has(id) ? x.delete(id) : x.add(id); return x; });

  const apply = async () => {
    setBusy(true);
    try {
      const items = data.items.filter((x) => selected.has(x.recipe_id)).map((x) => ({
        recipe_id: x.recipe_id,
        ingredients: x.ingredients.map((l) => (l.ingredient_id
          ? { ingredient_id: l.ingredient_id, quantity: l.quantity, unit: l.unit }
          : { quantity: l.quantity, unit: l.unit, new_ingredient: { name: l.name, unit: l.ingredient_unit, unit_cost: l.estimated_unit_cost, category: l.category } })),
      }));
      const { data: res } = await api.post("/recipes/apply-ingredients", { items });
      track("ingredients_ai_filled", { recipes: res.updated, new_ingredients: res.ingredients_created });
      toast.success(t("aiFillDialog.x_recette_s_completee_s_x_ingredient_s_cre", { v0: res.updated, v1: res.ingredients_created }));
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="ai-fill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[hsl(var(--ai))]" />{t("aiFillDialog.ingredients_en_1_clic")}</DialogTitle>
          <DialogDescription>{t("aiFillDialog.l_ia_propose_ingredients_et_quantites_pour")}</DialogDescription>
        </DialogHeader>
        {error ? <p className="py-8 text-center text-sm text-rose-600 dark:text-rose-400" data-testid="ai-fill-error">{error}</p> : !data ? <Loading /> : (
          <>
            <p className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-muted-foreground" data-testid="ai-fill-estimate-note">
              {t("aiFillDialog.les_prix_d_achat_marques")} <span className="font-semibold text-amber-600 dark:text-amber-400">{t("common.estime")}</span> {t("aiFillDialog.sont_des_moyennes_du_marche_importez_vos_f")}
            </p>
            <ul className="max-h-[45vh] divide-y overflow-y-auto rounded-lg border">
              {data.items.map((it, i) => <Row key={it.recipe_id} it={it} i={i} target={target} checked={selected.has(it.recipe_id)} onToggle={() => toggle(it.recipe_id)} />)}
            </ul>
            {data.failed > 0 && <p className="text-xs text-muted-foreground">{data.failed} {t("aiFillDialog.plat_s_n_ont_pas_pu_etre_completes_relance")}</p>}
          </>
        )}
        <DialogFooter>
          <Button onClick={apply} disabled={!data || busy || !selected.size} className="w-full sm:w-auto" data-testid="ai-fill-apply-btn">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("aiFillDialog.appliquer_a")} {t("common.n_dishes_paren", { v0: selected.size })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
